import { useEffect, useMemo, useState } from 'react';
import { id } from '@instantdb/react';
import { db } from '../db/client';
import type { Comment, Reply } from '../db/types';
import { avatarColor, avatarInitial, displayName } from '../widget/avatar';
import { relativeTime } from '../widget/relativeTime';

type ReplyWithAuthor = Reply & { author?: { id: string; email: string } | null };

interface PinPopoverProps {
  comment: Comment & {
    author?: { id: string; email: string } | null;
    replies?: ReplyWithAuthor[];
  };
  currentUserId: string;
  currentUserEmail: string;
  anchorLeft: number;
  anchorTop: number;
  onClose: () => void;
}

export function PinPopover({
  comment,
  currentUserId,
  currentUserEmail,
  anchorLeft,
  anchorTop,
  onClose,
}: PinPopoverProps) {
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  async function toggleResolved() {
    const next = comment.status === 'resolved' ? 'open' : 'resolved';
    try {
      await db.transact([db.tx.comments[comment.id].update({ status: next })]);
    } catch (err) {
      alert('Could not update: ' + (err as Error).message);
    }
  }

  async function deleteComment() {
    if (!confirm('Delete this comment and all its replies?')) return;
    try {
      const replyDeletes = (comment.replies ?? []).map((r) =>
        db.tx.replies[r.id].delete(),
      );
      await db.transact([
        ...replyDeletes,
        db.tx.comments[comment.id].delete(),
      ]);
      onClose();
    } catch (err) {
      alert('Could not delete: ' + (err as Error).message);
    }
  }

  const replies = useMemo(
    () => [...(comment.replies ?? [])].sort((a, b) => a.createdAt - b.createdAt),
    [comment.replies],
  );

  // Position card to the right of the pin, but flip to the left if it would
  // overflow the viewport.
  const CARD_WIDTH = 360;
  const flipLeft = anchorLeft + 40 + CARD_WIDTH > window.scrollX + window.innerWidth - 16;
  const left = flipLeft
    ? Math.max(8, anchorLeft - CARD_WIDTH - 16)
    : anchorLeft + 40;
  const top = Math.max(window.scrollY + 12, anchorTop - 16);

  const canEditComment = comment.author?.id === currentUserId;
  const isResolved = comment.status === 'resolved';

  return (
    <div
      className={'uin-card' + (isResolved ? ' resolved' : '')}
      style={{ top, left, width: CARD_WIDTH }}
      onClick={(e) => e.stopPropagation()}
    >
      <header className="uin-card-header">
        <span className="uin-card-title">{isResolved ? 'Resolved' : 'Comment'}</span>
        <div className="uin-card-actions">
          <div className="uin-menu">
            <button
              className="uin-icon-btn"
              aria-label="More"
              title="More"
              onClick={() => setMenuOpen((v) => !v)}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <circle cx="3" cy="8" r="1.4" />
                <circle cx="8" cy="8" r="1.4" />
                <circle cx="13" cy="8" r="1.4" />
              </svg>
            </button>
            {menuOpen && (
              <div className="uin-menu-pop" onMouseLeave={() => setMenuOpen(false)}>
                {canEditComment ? (
                  <button
                    onClick={() => {
                      setMenuOpen(false);
                      deleteComment();
                    }}
                  >
                    Delete thread
                  </button>
                ) : (
                  <button disabled style={{ color: '#94a3b8', cursor: 'default' }}>
                    No actions
                  </button>
                )}
              </div>
            )}
          </div>

          <button
            className={'uin-icon-btn' + (isResolved ? ' active' : '')}
            aria-label={isResolved ? 'Reopen thread' : 'Resolve thread'}
            title={isResolved ? 'Reopen thread' : 'Resolve thread'}
            onClick={toggleResolved}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12l5 5L20 7" />
            </svg>
          </button>
          <button
            className="uin-icon-btn"
            aria-label="Close"
            title="Close"
            onClick={onClose}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
      </header>

      <div className="uin-card-thread">
        <Message
          email={comment.author?.email ?? comment.authorEmail}
          body={comment.body}
          createdAt={comment.createdAt}
          onDelete={canEditComment ? deleteComment : undefined}
        />

        {replies.map((r) => (
          <ReplyMessage
            key={r.id}
            reply={r}
            currentUserId={currentUserId}
            ownerCanDelete={canEditComment}
          />
        ))}
      </div>

      <ReplyInput
        commentId={comment.id}
        currentUserId={currentUserId}
        currentUserEmail={currentUserEmail}
      />
    </div>
  );
}

function Message({
  email,
  body,
  createdAt,
  onDelete,
}: {
  email: string;
  body: string;
  createdAt: number;
  onDelete?: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const color = avatarColor(email);
  return (
    <div className="uin-msg">
      <div className="uin-msg-avatar" style={{ background: color.bg, color: color.fg }}>
        {avatarInitial(email)}
      </div>
      <div className="uin-msg-content">
        <div className="uin-msg-head">
          <span className="uin-msg-name">{displayName(email)}</span>
          <span className="uin-msg-time">{relativeTime(createdAt)}</span>
          {onDelete && (
            <div className="uin-menu">
              <button
                className="uin-icon-btn small"
                aria-label="Message options"
                onClick={() => setMenuOpen((v) => !v)}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor">
                  <circle cx="3" cy="8" r="1.2" />
                  <circle cx="8" cy="8" r="1.2" />
                  <circle cx="13" cy="8" r="1.2" />
                </svg>
              </button>
              {menuOpen && (
                <div className="uin-menu-pop" onMouseLeave={() => setMenuOpen(false)}>
                  <button onClick={() => { setMenuOpen(false); onDelete(); }}>Delete</button>
                </div>
              )}
            </div>
          )}
        </div>
        <div className="uin-msg-body">{body}</div>
      </div>
    </div>
  );
}

function ReplyMessage({
  reply,
  currentUserId,
  ownerCanDelete,
}: {
  reply: ReplyWithAuthor;
  currentUserId: string;
  ownerCanDelete: boolean;
}) {
  const isMine = reply.author?.id === currentUserId;
  const canDelete = isMine || ownerCanDelete;
  return (
    <Message
      email={reply.author?.email ?? reply.authorEmail}
      body={reply.body}
      createdAt={reply.createdAt}
      onDelete={
        canDelete
          ? async () => {
              if (!confirm('Delete this reply?')) return;
              try {
                await db.transact([db.tx.replies[reply.id].delete()]);
              } catch (err) {
                alert('Could not delete: ' + (err as Error).message);
              }
            }
          : undefined
      }
    />
  );
}

function ReplyInput({
  commentId,
  currentUserId,
  currentUserEmail,
}: {
  commentId: string;
  currentUserId: string;
  currentUserEmail: string;
}) {
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    const trimmed = body.trim();
    if (!trimmed) return;
    setBusy(true);
    try {
      const replyId = id();
      await db.transact([
        db.tx.replies[replyId].update({
          body: trimmed,
          authorEmail: currentUserEmail.trim().toLowerCase(),
          createdAt: Date.now(),
        }),
        db.tx.replies[replyId].link({ comment: commentId, author: currentUserId }),
      ]);
      setBody('');
    } catch (err) {
      alert('Could not reply: ' + (err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const me = avatarColor(currentUserEmail);
  const canSubmit = body.trim().length > 0 && !busy;

  return (
    <form
      className="uin-reply-row"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="uin-reply-avatar" style={{ background: me.bg, color: me.fg }}>
        {avatarInitial(currentUserEmail)}
      </div>
      <div className={'uin-reply-pill' + (canSubmit ? ' ready' : '')}>
        <textarea
          className="uin-reply-textarea"
          placeholder="Reply"
          rows={1}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') submit();
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              submit();
            }
          }}
        />
        <button
          type="submit"
          className="uin-reply-send"
          aria-label="Send reply"
          disabled={!canSubmit}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 19V5" />
            <path d="M5 12l7-7 7 7" />
          </svg>
        </button>
      </div>
    </form>
  );
}
